import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { formatDateOnly } from '../../../common/utils/date';
import { fmtMoney, digitsOnly, groupThousands } from '../../../common/utils/money';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useTariffs, useCreateTariff, useDeleteTariff } from '../contexts/tariffs';

import '../styles/billing.css';

const today = () => new Date().toLocaleDateString('en-CA');
const SOURCE_LABEL = { manual: 'Manual', computed_from_lines: 'Desde composición' };

function MoneyInput({ value, onChange, disabled }) {
    return (
        <span className="billing-money">
            <span className="billing-money__sign">$</span>
            <input type="text" inputMode="numeric" value={groupThousands(value)} onChange={(e) => onChange(digitsOnly(e.target.value))} disabled={disabled} placeholder="0" />
        </span>
    );
}

// Tarifa versionada de un manejo (admin-billing-tariffs.md). Vigencias medio-abiertas
// sin solape; una nueva tarifa CIERRA la vigente e inserta la abierta (cerrar-e-insertar).
export default function TariffModal({ management, onClose }) {
    const id = management.id;
    const { data, isLoading, error } = useTariffs(id);
    const { mutateAsync: create, isPending: creating } = useCreateTariff();
    const { mutateAsync: remove, isPending: removing } = useDeleteTariff();
    const busy = creating || removing;
    const items = data?.items || [];
    const cardRef = useModalDialog(onClose);
    const confirm = useConfirm();

    const [rate, setRate] = useState('');
    const [from, setFrom] = useState(today());

    const add = async () => {
        if (rate === '' || rate == null) { toast.error('Ingresá la tarifa por día'); return; }
        if (!from) { toast.error('Elegí la fecha de vigencia'); return; }
        try {
            await create({ management_id: id, effective_from: from, day_rate: Number(rate) });
            toast.success('Tarifa registrada');
            setRate('');
        } catch (er) {
            if (er?.status === 409) toast.error('La vigencia cruza otra activa — revisá la fecha');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo registrar la tarifa');
        }
    };
    const del = async (t) => {
        const ok = await confirm({
            title: 'Eliminar tarifa',
            message: '¿Eliminar esta vigencia de tarifa? (corrección — el flujo normal es cerrar-e-insertar)',
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(t.id); toast.success('Tarifa eliminada'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <div className="billing-modal" role="dialog" aria-modal="true" aria-label="Tarifas del manejo">
            <div className="billing-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="billing-modal__card billing-modal__card--wide" ref={cardRef}>
                <header className="billing-modal__head">
                    <div>
                        <h2 className="billing-modal__title">Tarifas</h2>
                        <span className="billing-modal__sub">{management.name} · nueva tarifa cierra la vigente (cerrar-e-insertar)</span>
                    </div>
                    <button type="button" className="billing-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {error ? (
                    <p className="billing-modal__error">No se pudo cargar el historial de tarifas.</p>
                ) : isLoading ? (
                    <p className="billing-modal__state">Cargando…</p>
                ) : (
                    <table className="cat-table billing-lines">
                        <thead>
                            <tr><th>Vigencia</th><th>Tarifa / día</th><th>Origen</th><th aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {items.map((t) => (
                                <tr key={t.id} className="cat-table__row">
                                    <td>
                                        {formatDateOnly(t.effective_from)} → {t.effective_to ? formatDateOnly(t.effective_to) : <span className="billing-tag billing-tag--open">vigente</span>}
                                    </td>
                                    <td>{fmtMoney(t.day_rate)}</td>
                                    <td>{SOURCE_LABEL[t.source] || t.source || '—'}</td>
                                    <td>
                                        <button type="button" className="billing-icon billing-icon--danger" onClick={() => del(t)} disabled={busy} aria-label="Eliminar" title="Eliminar (corrección)"><IconTrash /></button>
                                    </td>
                                </tr>
                            ))}
                            {!items.length && <tr><td colSpan={4} className="cat-table__empty" style={{ textAlign: 'center' }}>Sin tarifas. Registrá la primera vigencia.</td></tr>}
                        </tbody>
                    </table>
                )}

                <div className="billing-add">
                    <span className="billing-add__title">Nueva vigencia</span>
                    <div className="billing-add__row">
                        <label className="billing-form__field">
                            <span className="billing-form__label">Tarifa por día</span>
                            <MoneyInput value={rate} onChange={setRate} disabled={busy} />
                        </label>
                        <label className="billing-form__field">
                            <span className="billing-form__label">Vigente desde</span>
                            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={busy} />
                        </label>
                        <button type="button" className="billing-btn billing-btn--primary" onClick={add} disabled={busy || rate === ''}>Agregar</button>
                    </div>
                </div>

                <footer className="billing-modal__foot">
                    <button type="button" className="billing-btn billing-btn--ghost" onClick={onClose}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
